import React, { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Input, TextArea, Select, Button } from "../../components/common/Primitives";
import { Plus, ArrowUpRight, Check, Clock, AlertTriangle, X, User, Zap } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { useAuth } from "../../lib/AuthContext";

const ENTITY_TYPES = ["startup", "investor", "interest", "deal_room", "query", "ticket", "other"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUSES = ["Open", "In Progress", "Blocked", "Done", "Cancelled"];

function priorityTone(p) { return p === "urgent" ? "danger" : p === "high" ? "warn" : p === "low" ? "muted" : "gold"; }
function statusTone(s) {
  return s === "Done" ? "success" : s === "Blocked" ? "danger" : s === "Cancelled" ? "muted" : s === "In Progress" ? "gold" : "warn";
}
function isOverdue(t) { return t.due_at && t.status !== "Done" && t.status !== "Cancelled" && new Date(t.due_at) < new Date(); }
function fmtDue(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffH = Math.round((d - new Date()) / 3600000);
  const rel = diffH < 0 ? `${Math.abs(diffH)}h overdue` : diffH < 24 ? `in ${diffH}h` : `in ${Math.round(diffH / 24)}d`;
  return `${formatDateTime(iso)} · ${rel}`;
}

export default function AdminTasks() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [tasks, setTasks] = useState([]);
  const [queue, setQueue] = useState({ overdue: [], unassigned: [], escalated: [] });
  const [team, setTeam] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [escalating, setEscalating] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", entity_type: "startup", entity_id: "",
    assignee_user_id: "", priority: "normal", sla_hours: 48,
  });

  const loadTeam = useCallback(async () => {
    try { const { data } = await api.get("/admin/team"); setTeam(data || []); } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadTeam(); }, [loadTeam]);

  const load = useCallback(async () => {
    try {
      if (tab === "mine") {
        const { data } = await api.get("/admin/tasks", { params: { mine: true } });
        setTasks(data || []);
      } else if (tab === "all") {
        const { data } = await api.get("/admin/tasks", { params: { limit: 200 } });
        setTasks(data || []);
      } else if (tab === "queue") {
        const { data } = await api.get("/admin/tasks/queue");
        setQueue(data || { overdue: [], unassigned: [], escalated: [] });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required.");
    try {
      const body = { ...form, sla_hours: parseInt(form.sla_hours) || 48 };
      if (!body.assignee_user_id) delete body.assignee_user_id;
      await api.post("/admin/tasks", body);
      toast.success("Task created.");
      setShowCreate(false);
      setForm({ title: "", description: "", entity_type: "startup", entity_id: "", assignee_user_id: "", priority: "normal", sla_hours: 48 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const update = async (task_id, patch) => {
    try {
      const { data } = await api.put(`/admin/tasks/${task_id}`, patch);
      toast.success("Updated.");
      if (selected?.task_id === task_id) setSelected(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const escalate = async () => {
    if (!escalating) return;
    try {
      await api.post(`/admin/tasks/${escalating.task_id}/escalate`, {
        escalate_to_user_id: escalating.target || undefined,
        reason: escalating.reason || undefined,
      });
      toast.success("Escalated.");
      setEscalating(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeList = tab === "queue" ? null : tasks;

  return (
    <DashboardShell
      title="Tasks & Escalations"
      subtitle="ADMIN · TEAM OPS"
      actions={<Button onClick={() => setShowCreate(true)} data-testid="tasks-new-btn"><Plus size={14} className="inline mr-1" /> New Task</Button>}
    >
      <Toaster richColors position="top-right" />

      {/* Team roster strip */}
      <div className="glass rounded-lg p-4 mb-4" data-testid="team-roster">
        <div className="overline mb-3">VenturaZ Team · Open Task Load</div>
        <div className="flex flex-wrap gap-3">
          {team.map((t) => (
            <div key={t.user_id} className="flex items-center gap-2 bg-white/60 border border-navy/10 rounded-full pl-2 pr-4 py-1.5" data-testid={`team-${t.role}`}>
              <div className="w-7 h-7 rounded-full bg-navy text-white flex items-center justify-center text-[10px] font-heading font-bold">
                {(t.name || t.email).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div>
                <div className="text-xs font-heading text-ink leading-tight">{t.name || t.email}</div>
                <div className="text-[10px] font-mono text-ink-muted uppercase tracking-wider">{t.role} · {t.open_task_count} open</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex glass rounded-full p-1 mb-4" data-testid="tasks-tabs">
        <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} label="My Tasks" testId="tasks-tab-mine" />
        <TabBtn active={tab === "queue"} onClick={() => setTab("queue")} label="Priority Queue" testId="tasks-tab-queue" />
        <TabBtn active={tab === "all"} onClick={() => setTab("all")} label="All Tasks" testId="tasks-tab-all" />
      </div>

      {tab !== "queue" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`tasks-list-${tab}`}>
          <div className={selected ? "lg:col-span-2" : "lg:col-span-3"}>
            <Card testId="task-list-card">
              <TaskTable tasks={activeList} onSelect={setSelected} team={team} onUpdate={update} onEscalateOpen={(t) => setEscalating({ ...t, target: "", reason: "" })} me={user} />
            </Card>
          </div>
          {selected && <div><TaskDrawer task={selected} onClose={() => setSelected(null)} team={team} onUpdate={update} onEscalateOpen={(t) => setEscalating({ ...t, target: "", reason: "" })} /></div>}
        </div>
      )}

      {tab === "queue" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <QueueColumn title="Overdue" icon={AlertTriangle} tone="danger" tasks={queue.overdue} onSelect={setSelected} team={team} onUpdate={update} onEscalateOpen={(t) => setEscalating({ ...t, target: "", reason: "" })} testId="queue-overdue" />
          <QueueColumn title="Unassigned" icon={User} tone="warn" tasks={queue.unassigned} onSelect={setSelected} team={team} onUpdate={update} onEscalateOpen={(t) => setEscalating({ ...t, target: "", reason: "" })} testId="queue-unassigned" />
          <QueueColumn title="Escalated" icon={Zap} tone="gold" tasks={queue.escalated} onSelect={setSelected} team={team} onUpdate={update} onEscalateOpen={(t) => setEscalating({ ...t, target: "", reason: "" })} testId="queue-escalated" />
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create Task" testId="task-create-modal">
          <div className="space-y-3">
            <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testId="task-title" placeholder="e.g. Review FinEdge Q2 financials" />
            <TextArea label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} testId="task-description" />
            <div className="grid grid-cols-2 gap-3">
              <Select label="Entity Type" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} testId="task-entity-type">
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              <Input label="Entity ID (optional)" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} testId="task-entity-id" placeholder="usr_startup_xyz" />
              <Select label="Assign To" value={form.assignee_user_id} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} testId="task-assignee">
                <option value="">Unassigned (queue)</option>
                {team.map((t) => {
                  const label = `${t.name || t.email} · ${t.role}`;
                  return <option key={t.user_id} value={t.user_id}>{label}</option>;
                })}
              </Select>
              <Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} testId="task-priority">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
              <Input label="SLA (hours)" type="number" min="1" value={form.sla_hours} onChange={(e) => setForm({ ...form, sla_hours: e.target.value })} testId="task-sla" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={create} className="flex-1" data-testid="task-create-confirm">Create Task</Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)} className="flex-1" data-testid="task-create-cancel">Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Escalation modal */}
      {escalating && (
        <Modal onClose={() => setEscalating(null)} title={`Escalate: ${escalating.title}`} testId="task-escalate-modal">
          <div className="space-y-3">
            <Select label="Escalate To" value={escalating.target} onChange={(e) => setEscalating({ ...escalating, target: e.target.value })} testId="escalate-target">
              <option value="">Auto (default super_admin)</option>
              {team.filter((t) => t.user_id !== escalating.assignee_user_id).map((t) => {
                const label = `${t.name || t.email} · ${t.role}`;
                return <option key={t.user_id} value={t.user_id}>{label}</option>;
              })}
            </Select>
            <TextArea label="Reason" value={escalating.reason} onChange={(e) => setEscalating({ ...escalating, reason: e.target.value })} testId="escalate-reason" placeholder="Why escalating?" />
            <div className="flex gap-2 pt-2">
              <Button onClick={escalate} className="flex-1" data-testid="escalate-confirm"><Zap size={14} className="inline mr-1" /> Escalate</Button>
              <Button variant="ghost" onClick={() => setEscalating(null)} className="flex-1" data-testid="escalate-cancel">Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </DashboardShell>
  );
}

function TabBtn({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-1.5 text-sm font-heading font-semibold rounded-full transition-all ${active ? "bg-navy text-white shadow-[0_2px_10px_rgba(15,61,46,0.15)]" : "text-ink-soft hover:text-ink"}`}
    >
      {label}
    </button>
  );
}

function TaskTable({ tasks, onSelect, team, onUpdate, onEscalateOpen, me }) {
  if (!tasks) return null;
  if (tasks.length === 0) return <div className="text-ink-muted text-sm text-center py-8">No tasks.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
          <tr className="border-b border-navy/10">
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2">Assignee</th>
            <th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">Priority</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Due</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.task_id} className={`border-b border-navy/5 hover:bg-navy/[0.02] transition-colors ${isOverdue(t) ? "bg-rose-500/[0.04]" : ""}`} data-testid={`task-row-${t.task_id}`}>
              <td className="px-3 py-2">
                <button onClick={() => onSelect(t)} className="text-ink font-heading text-left hover:text-[#8A6600]">
                  {t.title}
                  {t.escalation_count > 0 && <span className="ml-2 text-[10px] text-[#8A6600] font-mono">↑ ESCALATED</span>}
                </button>
              </td>
              <td className="px-3 py-2 text-xs text-ink-muted font-mono">{t.assignee_email || <span className="italic">unassigned</span>}</td>
              <td className="px-3 py-2 text-xs text-ink-muted font-mono">{t.entity_type}{t.entity_id ? `/${t.entity_id.slice(0, 10)}` : ""}</td>
              <td className="px-3 py-2"><Pill tone={priorityTone(t.priority)}>{t.priority}</Pill></td>
              <td className="px-3 py-2"><Pill tone={statusTone(t.status)}>{t.status}</Pill></td>
              <td className={`px-3 py-2 text-[11px] font-mono ${isOverdue(t) ? "text-rose-700" : "text-ink-muted"}`}>{fmtDue(t.due_at)}</td>
              <td className="px-3 py-2 flex gap-1">
                {t.status !== "Done" && t.status !== "Cancelled" && (
                  <>
                    <button onClick={() => onUpdate(t.task_id, { status: "Done" })} className="p-1 text-emerald-700 hover:bg-emerald-500/10 rounded" title="Mark done" data-testid={`task-done-${t.task_id}`}><Check size={14} /></button>
                    <button onClick={() => onEscalateOpen(t)} className="p-1 text-[#8A6600] hover:bg-gold/10 rounded" title="Escalate" data-testid={`task-escalate-${t.task_id}`}><ArrowUpRight size={14} /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueueColumn({ title, icon: Icon, tone, tasks, onSelect, team, onUpdate, onEscalateOpen, testId }) {
  return (
    <Card testId={testId}>
      <div className="overline mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2"><Icon size={14} className={tone === "danger" ? "text-rose-600" : tone === "warn" ? "text-amber-600" : "text-[#8A6600]"} /> {title}</span>
        <span className="font-mono text-ink">{tasks.length}</span>
      </div>
      <div className="space-y-2 max-h-[520px] overflow-y-auto">
        {tasks.length === 0 && <div className="text-ink-faint text-xs">Nothing here — you&apos;re clear.</div>}
        {tasks.map((t) => (
          <div key={t.task_id} className={`border rounded-md p-3 ${isOverdue(t) ? "border-rose-300 bg-rose-50/40" : "border-navy/10"}`} data-testid={`queue-task-${t.task_id}`}>
            <button onClick={() => onSelect(t)} className="text-sm text-ink font-heading text-left w-full hover:text-[#8A6600]">{t.title}</button>
            <div className="flex flex-wrap gap-1 mt-2">
              <Pill tone={priorityTone(t.priority)}>{t.priority}</Pill>
              <Pill tone={statusTone(t.status)}>{t.status}</Pill>
              {t.assignee_user_id
                ? <span className="text-[10px] font-mono text-ink-muted self-center">→ {t.assignee_email || t.assignee_user_id.slice(0, 12)}</span>
                : <span className="text-[10px] font-mono text-amber-700 italic self-center">unassigned</span>}
            </div>
            <div className="text-[10px] font-mono text-ink-muted mt-1">Due: {fmtDue(t.due_at)}</div>
            <div className="flex gap-1 mt-2">
              {t.status !== "Done" && (
                <>
                  <button onClick={() => onUpdate(t.task_id, { status: "Done" })} className="px-2 py-1 text-[10px] font-heading text-emerald-700 hover:bg-emerald-500/10 rounded" data-testid={`queue-done-${t.task_id}`}>DONE</button>
                  <button onClick={() => onEscalateOpen(t)} className="px-2 py-1 text-[10px] font-heading text-[#8A6600] hover:bg-gold/10 rounded" data-testid={`queue-escalate-${t.task_id}`}>ESCALATE</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TaskDrawer({ task, onClose, team, onUpdate, onEscalateOpen }) {
  return (
    <Card testId="task-drawer">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Task Detail</div>
        <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="task-drawer-close"><X size={16} /></button>
      </div>
      <div className="font-heading text-ink text-lg mb-1">{task.title}</div>
      <div className="text-xs font-mono text-ink-muted mb-3">{task.task_id}</div>
      <div className="flex flex-wrap gap-1 mb-3">
        <Pill tone={priorityTone(task.priority)}>{task.priority}</Pill>
        <Pill tone={statusTone(task.status)}>{task.status}</Pill>
        {task.escalation_count > 0 && <Pill tone="gold">↑ {task.escalation_count}× escalated</Pill>}
      </div>
      {task.description && <div className="text-sm text-ink-soft mb-3 whitespace-pre-wrap">{task.description}</div>}
      <div className="text-xs space-y-1 mb-4">
        <Row k="Entity" v={`${task.entity_type}${task.entity_id ? ` · ${task.entity_id}` : ""}`} />
        <Row k="Assignee" v={task.assignee_email || "unassigned"} />
        <Row k="Assigned by" v={task.assigned_by_email} />
        <Row k="Due" v={fmtDue(task.due_at)} />
        {task.escalated_by && <Row k="Escalated by" v={task.escalated_by} />}
      </div>

      {task.status !== "Done" && task.status !== "Cancelled" && (
        <div className="space-y-2 mb-3">
          <Select value={task.assignee_user_id || ""} onChange={(e) => onUpdate(task.task_id, { assignee_user_id: e.target.value })} testId="task-drawer-reassign" label="Reassign">
            <option value="">Unassigned</option>
            {team.map((t) => {
              const label = `${t.name || t.email} · ${t.role}`;
              return <option key={t.user_id} value={t.user_id}>{label}</option>;
            })}
          </Select>
          <Select value={task.status} onChange={(e) => onUpdate(task.task_id, { status: e.target.value })} testId="task-drawer-status" label="Status">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Select value={task.priority} onChange={(e) => onUpdate(task.task_id, { priority: e.target.value })} testId="task-drawer-priority" label="Priority">
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => onUpdate(task.task_id, { status: "Done" })} className="flex-1" data-testid="task-drawer-done">Mark Done</Button>
            <Button variant="ghost" onClick={() => onEscalateOpen(task)} className="flex-1" data-testid="task-drawer-escalate">Escalate</Button>
          </div>
        </div>
      )}

      {task.history?.length > 0 && (
        <div>
          <div className="overline mb-2">History</div>
          <div className="space-y-1 text-[11px] font-mono text-ink-muted max-h-40 overflow-y-auto">
            {task.history.slice().reverse().map((h, i) => (
              <div key={i}>
                <span className="text-ink-faint">{h.at?.slice(11, 16)}</span>
                {" · "}{h.by}
                {" "}<span className="text-[#8A6600]">{h.action}</span>
                {h.to && ` → ${h.to}`}
                {h.reason && `: "${h.reason}"`}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><span className="text-ink-muted">{k}</span><span className="text-ink text-right">{v}</span></div>;
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div className="glass rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div className="font-heading text-ink text-lg">{title}</div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
